// src/components/AdminDashboard/sections/RevenueRecordsExportSection.jsx
import React,{ useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, FormControl, MenuItem, Select, InputLabel, Button } from '@mui/material';
import CloudDownloadOutlined from '@mui/icons-material/CloudDownloadOutlined';

const listYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 5; i++) {
    years.push(currentYear - i);
  }
  return years;
};

export const YEAR_OPTIONS = listYears();

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const RevenueRecordsExportSection = ({ selectedMonths, selectedYear, handleYearChange, handleMonthChange, handleExportRevenue, exportError }) => {

  return (
    <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-8 mb-8 shadow-lg border border-gray-100 hover:shadow-xl hover:border-[#00a693]/20 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-1 before:bg-gradient-to-r before:from-[#00a693] before:to-[#ff6f3c] before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300">
      {/* Section Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-[#00a693]/10">
        <div>
          <Typography variant="h6" className="text-[#5a7684] font-bold text-xl mb-2">
            Revenue Records
          </Typography>
          <Typography variant="body2" className="text-[#666666] text-sm">
            Export revenue data for specific time periods.
          </Typography>
        </div>
      </div>

      {/* Error Message */}
      {exportError && (
        <div className="text-center mb-4 text-[#BD2026] bg-red-50/80 backdrop-blur-sm border border-[#BD2026]/20 rounded-xl p-4 shadow-lg font-semibold">
          {exportError}
        </div>
      )}

      {/* Form Controls */}
      <div className="flex flex-wrap gap-4 items-center p-6 bg-gradient-to-br from-[#00a693]/5 to-[#ff6f3c]/5 rounded-xl border border-[#00a693]/10">
        <FormControl variant="outlined" className="min-w-[120px]">
          <InputLabel className="text-[#5a7684] font-semibold [&.Mui-focused]:text-[#00a693]">Select Year</InputLabel>
          <Select
            labelId="year-select-label"
            label="Select Year"
            value={selectedYear}
            onChange={handleYearChange}
            className="[&_.MuiOutlinedInput-notchedOutline]:border-gray-300 [&_.MuiOutlinedInput-notchedOutline]:rounded-lg hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          >
            {YEAR_OPTIONS.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" className="min-w-[200px]">
          <InputLabel className="text-[#5a7684] font-semibold [&.Mui-focused]:text-[#00a693]">Select Months</InputLabel>
          <Select
            labelId="month-select-label"
            label="Select Months"
            multiple
            value={selectedMonths}
            onChange={handleMonthChange}
            renderValue={(selected) => {
              const selectedLabels = MONTH_OPTIONS
                .filter((opt) => selected.includes(opt.value))
                .map((opt) => opt.label);
              return selectedLabels.join(', ');
            }}
            className="[&_.MuiOutlinedInput-notchedOutline]:border-gray-300 [&_.MuiOutlinedInput-notchedOutline]:rounded-lg hover:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:border-[#00a693] focus:[&_.MuiOutlinedInput-notchedOutline]:shadow-[0_0_0_2px_rgba(0,166,147,0.2)]"
          >
            {MONTH_OPTIONS.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button 
          onClick={handleExportRevenue}
          className="bg-gradient-to-r from-[#BD2026] to-[#ff6f3c] hover:from-[#ff6f3c] hover:to-[#BD2026] text-white rounded-lg px-6 py-3 font-semibold text-sm shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-2"
        >
          <CloudDownloadOutlined className="text-lg" />
          Export Revenue to Excel
        </Button>
      </div>
    </div>
  );
};

RevenueRecordsExportSection.propTypes = {
  selectedMonths: PropTypes.array.isRequired,
  selectedYear: PropTypes.number.isRequired,
  handleYearChange: PropTypes.func.isRequired,
  handleMonthChange: PropTypes.func.isRequired,
  handleExportRevenue: PropTypes.func.isRequired,
  exportError: PropTypes.string,
};

export default RevenueRecordsExportSection;
